import { useState } from 'react';
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Input,
  Portal,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import { capitalizeFirstLetter } from '@utils/formats';
import ErrorLabel from '@components/ErrorLabel';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';
import api from '@services/api';

interface Props {
  nextOrder: number;
  onClose: () => void;
  onCreated: () => void;
}

const CreateStageDialog = ({ nextOrder, onClose, onCreated }: Props) => {
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = capitalizeFirstLetter((formData.get('name') as string)?.trim());

    if (!name || name.length < 3) {
      setNameError('O nome deve ter ao menos 3 caracteres.');
      return;
    }

    setNameError('');
    setLoading(true);

    try {
      const response = await api.post('/sales_funnel', { name, order: nextOrder });

      if (response.status !== 200) {
        toast.error('Erro ao criar etapa.');
        return;
      }

      onCreated();
      toast.success('Nova etapa criada!');
    } catch (error) {
      toast.error('Erro ao criar etapa. Tente novamente.');
      datadogRum.addError(new Error('Fail to create sales funnel node'), { error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root
      size="sm"
      placement="center"
      open
      onOpenChange={onClose}
      closeOnInteractOutside={false}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Nova etapa</Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body display="flex" flexDirection="column" gap={4}>
                <Field.Root invalid={!!nameError}>
                  <Field.Label>Nome da etapa</Field.Label>
                  <Input
                    name="name"
                    placeholder="Ex: Primeiro contato"
                    variant="subtle"
                    minLength={3}
                    required
                    autoFocus
                  />
                  {nameError && <ErrorLabel text={nameError} />}
                </Field.Root>

                <Field.Root>
                  <Field.Label>Ordem</Field.Label>
                  <Input value={nextOrder} variant="subtle" readOnly disabled />
                  <Field.HelperText>Definida automaticamente.</Field.HelperText>
                </Field.Root>
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Criar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CreateStageDialog;
